import { call, put, takeEvery } from 'redux-saga/effects';
import request from '../../utils/request';
import errorMessages from '../../components/Error/messages';
import { getAuthToken, setAuthToken, removeAuthToken } from '../Auth/token';
import {
  API_URL_WEBMASTER_INFO,
  API_URL_LOGIN,
  API_URL_SIGNUP,
  API_URL_SIGNUP_CONFIRMATION,
  API_URL_FORGOT,
  API_URL_FORGOT_CONFIRMATION,
  API_URL_LOGOUT,
  API_URL_SIGN_CAMPAIGN,
  API_URL_CAMPAIGN_LIST,
} from '../../config';
import {
  LOGIN_REQUEST,
  SIGNUP_REQUEST,
  SIGNUP_CONFIRMATION_REQUEST,
  LOGOUT_REQUEST,
  FORGOT_REQUEST,
  FORGOT_CONFIRMATION_REQUEST,
  VERIFY_REQUEST,
} from './constants';
import {
  loginSuccess,
  loginError,
  signupSuccess,
  signupError,
  signupConfirmationSuccess,
  signupConfirmationError,
  forgotSuccess,
  forgotError,
  forgotConfirmationSuccess,
  forgotConfirmationError,
  logoutSuccess,
  verifySuccess,
  verifyError,
} from './actions';

/**
 * VERIFY
 */

function verifyRequest(token) {
  const url = `${API_URL_WEBMASTER_INFO}?data=${JSON.stringify({ token })}`;
  const options = {
    method: 'GET',
  };

  return request(url, options);
}

function* verifyFlow() {
  try {
    const token = yield getAuthToken();
    if (token) {
      const result = yield call(verifyRequest, token);
      const { response } = result;
      if (response.success === 1) {
        const data = response.data;
        if (data.member_id === undefined) {
          data.member_id = '';
        }
        yield put(verifySuccess(data));
        return;
      }
    }
    throw errorMessages.defaultMessage;
  } catch (error) {
    yield removeAuthToken();
    yield put(verifyError());
  }
}

/**
 * LOGIN
 */

function loginRequest(action) {
  const { login, password } = action.attributes;
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({ login, password })}`,
  };

  return request(API_URL_LOGIN, options);
}

function signCampaignRequest(token) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({ token, campaign_id: '2' })}`,
  };

  return request(API_URL_SIGN_CAMPAIGN, options);
}

function getCampaignListRequest(token) {
  const data = JSON.stringify({
    token,
    connected: '1',
  });
  const url = `${API_URL_CAMPAIGN_LIST}?data=${data}`;
  const options = {
    method: 'GET',
  };

  return request(url, options);
}

function* loginFlow(action) {
  try {
    let result = yield call(loginRequest, action);
    let { response } = result;
    if (response.success !== 1) {
      throw errorMessages.defaultMessage;
    }

    const token = result.response.data.token;
    yield setAuthToken(token, action.attributes.isRememberMe);

    result = yield call(getCampaignListRequest, token);
    response = result.response;

    if (response.success === 1 && response.data.length === 0) {
      yield call(signCampaignRequest, token);
    }
    yield put(loginSuccess());
    yield verifyFlow();
  } catch (error) {
    yield put(loginError(error));
  }
}

/**
 * SIGNUP
 */

function signupRequest(action) {
  const { login, email, password } = action.attributes;
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({
      login,
      email,
      password,
      is_webmaster: 1,
      is_partner: 0,
    })}`,
  };

  return request(API_URL_SIGNUP, options);
}

function* signupFlow(action) {
  try {
    const result = yield call(signupRequest, action);
    const { response } = result;
    if (response.success !== 1) {
      throw errorMessages.defaultMessage;
    }
    yield put(signupSuccess());
  } catch (error) {
    yield put(signupError(error));
  }
}

/**
 * SIGNUP CONFIRMATION
 */

function signupConfirmationRequest(action) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({ confirm_code: action.code })}`,
  };

  return request(API_URL_SIGNUP_CONFIRMATION, options);
}

function* signupConfirmationFlow(action) {
  try {
    const result = yield call(signupConfirmationRequest, action);
    const { response } = result;
    if (response.success !== 1) {
      throw errorMessages.defaultMessage;
    }
    yield put(signupConfirmationSuccess());
  } catch (error) {
    yield put(signupConfirmationError(error));
  }
}

/**
 * FORGOT
 */

function forgotRequest(action) {
  const { email } = action.attributes;
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({ email })}`,
  };

  return request(API_URL_FORGOT, options);
}

function* forgotFlow(action) {
  try {
    const result = yield call(forgotRequest, action);
    const { response } = result;
    if (response.success !== 1) {
      throw errorMessages.defaultMessage;
    }
    yield put(forgotSuccess());
  } catch (error) {
    yield put(forgotError(error));
  }
}

/**
 * FORGOT CONFIRMATION
 */

function forgotConfirmationRequest(action) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({ recovery_code: action.code })}`,
  };

  return request(API_URL_FORGOT_CONFIRMATION, options);
}

function* forgotConfirmationFlow(action) {
  try {
    const result = yield call(forgotConfirmationRequest, action);
    const { response } = result;
    if (response.success !== 1) {
      throw errorMessages.defaultMessage;
    }
    yield put(forgotConfirmationSuccess());
  } catch (error) {
    yield put(forgotConfirmationError(error));
  }
}

/**
 * LOGOUT
 */

function logoutRequest(token) {
  const options = {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `data=${JSON.stringify({ token })}`,
  };

  return request(API_URL_LOGOUT, options);
}

function* logoutFlow() {
  try {
    const token = yield getAuthToken();
    if (token) {
      yield removeAuthToken();
      yield put(logoutSuccess()); // for optimistic update
      yield call(logoutRequest, token);
    }
    // eslint-disable-next-line
  } catch (error) {}
}

// run in parallel
export default function* rootSaga() {
  yield takeEvery(LOGIN_REQUEST, loginFlow);
  yield takeEvery(SIGNUP_REQUEST, signupFlow);
  yield takeEvery(SIGNUP_CONFIRMATION_REQUEST, signupConfirmationFlow);
  yield takeEvery(LOGOUT_REQUEST, logoutFlow);
  yield takeEvery(FORGOT_REQUEST, forgotFlow);
  yield takeEvery(FORGOT_CONFIRMATION_REQUEST, forgotConfirmationFlow);
  yield takeEvery(VERIFY_REQUEST, verifyFlow);
}



// WEBPACK FOOTER //
// ./src/containers/Auth/sagas.js